import React from 'react'
import ReactDOM from 'react-dom/client'
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SESSIONS_KEY } from './components/CraftingSessionsNav'
import { toast } from './lib/toasts'
import { work } from './lib/working'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { OrgProvider } from './hooks/useOrg'
import { SessionProvider } from './hooks/useSession'
import { SpokeProvider } from './hooks/useSpoke'
import { meta } from '@skin/meta'
import '@skin/tokens.css'
import './components/components.css'

document.title = meta.productName

// A crafting message carries meta ({ working, label, done, href }): the session is marked as working while it is with Scout,
// and when the reply lands — wherever the rep is by then — the spinner stops, the menu refreshes and a toast says so. The
// callbacks live on the cache, not on the page, so leaving the page loses none of it (2026-10-07).
interface CraftMeta { working?: string; label?: string; done?: string; href?: string }
const queryClient: QueryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, staleTime: 30_000 } },
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const meta = (mutation.meta ?? {}) as CraftMeta
      if (meta.done) toast({ title: meta.done, href: meta.href, kind: 'done' })
    },
    onError: (error, _vars, _ctx, mutation) => {
      const meta = (mutation.meta ?? {}) as CraftMeta
      if (meta.working) toast({ title: `${meta.label ?? 'The draft'}: Scout could not answer`, body: error instanceof Error ? error.message : String(error), href: meta.href, kind: 'error' })
    },
    onSettled: (_data, _error, _vars, _ctx, mutation) => {
      const meta = (mutation.meta ?? {}) as CraftMeta
      if (meta.working) { work.end(meta.working); void queryClient.invalidateQueries({ queryKey: [SESSIONS_KEY] }) }
    },
  }),
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SpokeProvider spoke={import.meta.env.VITE_SCOUT_SPOKE ?? 'ppp'}>
          {/* Inside the query client: changing the org resets every query, so it needs one. */}
          <OrgProvider>
            <SessionProvider>
              <App />
            </SessionProvider>
          </OrgProvider>
        </SpokeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
